import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["checkbox", "selectAll", "menu"];
  static values = { idsParam: { type: String, default: "ids" } };

  connect() {
    this.#refresh();
    document.addEventListener("click", this.#onDocumentClick);
  }

  disconnect() {
    document.removeEventListener("click", this.#onDocumentClick);
  }

  toggleAll({ target }) {
    this.checkboxTargets.forEach((cb) => {
      cb.checked = target.checked;
    });
    this.#refresh();
  }

  toggleCheckbox() {
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked =
        this.checkboxTargets.length > 0 &&
        this.checkboxTargets.every((cb) => cb.checked);
    }
    this.#refresh();
  }

  checkboxTargetConnected() {
    this.#refresh();
  }

  checkboxTargetDisconnected() {
    this.#refresh();
  }

  #selectedIds() {
    return this.checkboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.value);
  }

  #refresh() {
    const display = this.#selectedIds().length === 0 ? "none" : "";
    this.menuTargets.forEach((el) => {
      el.style.display = display;
    });
  }

  // The dropdown menu is portalled to <body> by the shared `dropdown`
  // controller (for positioning), which breaks Stimulus's own scoped
  // data-action bindings — so this delegates from document instead.
  #onDocumentClick = (event) => {
    const button = event.target.closest(
      "[data-bulk-action-url], [data-bulk-action-url-template]"
    );
    if (!button) return;

    if (button.dataset.bulkActionUrlTemplate) {
      this.#selectedIds().forEach((id) => {
        window.open(
          button.dataset.bulkActionUrlTemplate.replace("__ID__", id),
          "_blank"
        );
      });
      return;
    }

    const {
      bulkActionUrl: url,
      bulkActionMethod: method,
      bulkActionConfirm: confirmMsg
    } = button.dataset;
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    if (!method || method === "get") {
      this.#download(url);
    } else {
      this.#mutate(url, method);
    }
  };

  // Real navigation so the browser streams/downloads the response natively
  // instead of buffering it as a JS blob.
  #download(url) {
    window.location.href = `${url}?${this.#idsParams()}`;
  }

  // fetch sends PATCH/DELETE directly, so no form or _method override is
  // needed; CSRF goes in a header instead of a form field.
  async #mutate(url, method) {
    const response = await fetch(url, {
      method: method.toUpperCase(),
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": this.#csrf(),
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "text/html" // fetch defaults to */*, unlike a real navigation
      },
      body: this.#idsParams()
    });
    if (response.redirected) window.location.href = response.url;
  }

  #idsParams() {
    const params = new URLSearchParams();
    this.#selectedIds().forEach((id) => {
      params.append(`${this.idsParamValue}[]`, id);
    });
    return params.toString();
  }

  #csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content ?? "";
  }
}
